import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import {
  DARK_MEDIA_QUERY,
  THEME_STORAGE_KEY,
  ThemeContext,
  applyThemePreference,
  resolveTheme,
  storedThemePreference,
  type ResolvedTheme,
  type ThemeContextValue,
  type ThemePreference,
} from "./themeState";

export function ThemeProvider({ children }: PropsWithChildren) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    storedThemePreference,
  );
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(storedThemePreference()),
  );

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // The selected theme still applies for this session when storage is blocked.
    }
    setPreferenceState(next);
    setResolvedTheme(applyThemePreference(next));
  }, []);

  useLayoutEffect(() => {
    setResolvedTheme(applyThemePreference(preference));
    if (
      preference !== "system" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const media = window.matchMedia(DARK_MEDIA_QUERY);
    const handleChange = () => setResolvedTheme(applyThemePreference("system"));
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, [preference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolvedTheme, setPreference }),
    [preference, resolvedTheme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
